import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Block, Document, Inline } from '@contentful/rich-text-types';
import { getBlogPostBySlug } from '@/lib/contentful';
import type { Entry } from 'contentful';
import type { BlogPostSkeleton } from '@/lib/contentful';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';

const richTextOptions = {
  // Several posts were pasted into Contentful as a single text blob, with paragraph
  // breaks surviving only as \n characters inside one text node — HTML collapses
  // those into a wall of text. Turn each \n into a real visual paragraph break.
  renderText: (text: string) =>
    text
      .split('\n')
      .flatMap((segment, i) =>
        i === 0 ? [segment] : [<span key={`br-${i}`} className="block mt-4" aria-hidden="true" />, segment]
      ),
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node: unknown, children: React.ReactNode) => (
      <p className="text-text-secondary leading-relaxed mb-5">{children}</p>
    ),
    [BLOCKS.HEADING_2]: (_node: unknown, children: React.ReactNode) => (
      <h2 className="font-heading text-2xl font-bold text-text-primary mt-10 mb-4">{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (_node: unknown, children: React.ReactNode) => (
      <h3 className="font-heading text-xl font-semibold text-text-primary mt-8 mb-3">{children}</h3>
    ),
    [BLOCKS.UL_LIST]: (_node: unknown, children: React.ReactNode) => (
      <ul className="list-disc list-inside space-y-2 mb-5 text-text-secondary">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_node: unknown, children: React.ReactNode) => (
      <ol className="list-decimal list-inside space-y-2 mb-5 text-text-secondary">{children}</ol>
    ),
    [BLOCKS.QUOTE]: (_node: unknown, children: React.ReactNode) => (
      <blockquote className="border-l-4 border-brand pl-5 my-6 italic text-text-secondary">{children}</blockquote>
    ),
    [INLINES.HYPERLINK]: (node: Block | Inline, children: React.ReactNode) => (
      <a href={node.data.uri as string} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
        {children}
      </a>
    ),
  },
};

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Entry<BlogPostSkeleton> | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    getBlogPostBySlug(slug)
      .then((entry) => {
        if (!entry) setNotFound(true);
        else setPost(entry);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <main className="bg-bg-light min-h-screen pt-32 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
      </main>
    );
  }

  if (notFound || !post) {
    return (
      <main className="bg-bg-light min-h-screen pt-32 text-center px-4">
        <SEO
          title="Artículo no encontrado"
          description="El artículo que buscas no está disponible."
          path={`/blog/${slug ?? ''}`}
          noindex
        />
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-4">Artículo no encontrado</h1>
        <Link to="/blog" className="text-brand hover:underline">← Volver al blog</Link>
      </main>
    );
  }

  const { title, excerpt, body, category, coverImage, publishDate, readTime } = post.fields;
  const imageUrl = coverImage && 'fields' in coverImage
    ? `https:${(coverImage as { fields: { file: { url: string } } }).fields.file.url}`
    : null;

  const formattedDate = publishDate
    ? new Date(publishDate as string).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  // BlogPosting + BreadcrumbList: prerendered into the static HTML — required for
  // article rich results and Google Discover eligibility.
  const canonicalUrl = `https://mienviocuba.com/blog/${slug ?? ''}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title as string,
    description: (excerpt as string) || undefined,
    image: imageUrl ?? 'https://mienviocuba.com/og-image.jpg',
    datePublished: (publishDate as string | undefined) || undefined,
    inLanguage: 'es',
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    author: { '@type': 'Organization', name: 'Mi Envío Cuba', url: 'https://mienviocuba.com' },
    publisher: {
      '@type': 'Organization',
      name: 'Mi Envío Cuba',
      logo: { '@type': 'ImageObject', url: 'https://mienviocuba.com/logo.png' },
    },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://mienviocuba.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://mienviocuba.com/blog' },
      { '@type': 'ListItem', position: 3, name: title as string, item: canonicalUrl },
    ],
  };

  return (
    <main className="bg-bg-light">
      <SEO
        title={title as string}
        description={(excerpt as string) || `Lee "${title as string}" en el blog de Mi Envío Cuba.`}
        path={`/blog/${slug ?? ''}`}
        image={imageUrl ?? undefined}
        type="article"
        publishedTime={publishDate as string | undefined}
        author="Mi Envío Cuba"
        keywords={(category as string) ? `${category}, envíos Cuba, blog Mi Envío Cuba` : undefined}
        jsonLd={[articleSchema, breadcrumbSchema]}
      />
      {/* Hero Image — below the fixed 52px nav (transparent over the page bg),
          rounded like the rest of the redesign instead of full-bleed under the bar. */}
      {imageUrl && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-[68px]">
          <div className="h-64 sm:h-96 overflow-hidden rounded-[24px]">
            <img src={imageUrl} alt={title as string} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back link */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-text-muted hover:text-brand mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Volver al blog
        </Link>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="px-3 py-1 rounded-full bg-brand/10 text-brand text-xs font-medium">
            {String(category ?? '').trim()}
          </span>
          {publishDate && (
            <span className="flex items-center gap-1 text-xs text-text-muted">
              <Calendar className="w-3 h-3" />
              {formattedDate}
            </span>
          )}
          {readTime && (
            <span className="flex items-center gap-1 text-xs text-text-muted">
              <Clock className="w-3 h-3" />
              {readTime as string}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary leading-tight mb-4">
          {title as string}
        </h1>

        {/* Excerpt */}
        {excerpt && (
          <p className="text-lg text-text-secondary leading-relaxed mb-8 border-b border-gray-200 pb-8">
            {excerpt as string}
          </p>
        )}

        {/* Body */}
        <article>
          {body && documentToReactComponents(body as Document, richTextOptions)}
        </article>
      </div>

      <Footer />
    </main>
  );
}
